import { Controller } from '@hotwired/stimulus';

const POPOVER_WIDTH = 280;
const POPOVER_MARGIN = 16;

export default class extends Controller {
  static targets = [
    'trigger', 'triggerText', 'input', 'popover', 'back', 'title',
    'yearGrid', 'monthGrid', 'dayPanel', 'dayGrid',
  ];

  static values = {
    mode: String,
    min: String,
    max: String,
    months: Array,
  };

  connect() {
    this.onDocumentMousedown = this.onDocumentMousedown.bind(this);
  }

  disconnect() {
    document.removeEventListener('mousedown', this.onDocumentMousedown);
  }

  toggle() {
    if (this.popoverTarget.hidden) this.open();
    else this.close();
  }

  open() {
    const selected = this.selectedParts();
    this.tempYear = selected ? selected.year : null;
    this.tempMonth = selected ? selected.month : null;
    this.alignPopover();
    this.popoverTarget.hidden = false;
    this.triggerTarget.setAttribute('aria-expanded', 'true');
    document.addEventListener('mousedown', this.onDocumentMousedown);
    this.showStep('year');
  }

  close(focusTrigger = false) {
    document.removeEventListener('mousedown', this.onDocumentMousedown);
    this.popoverTarget.hidden = true;
    this.triggerTarget.setAttribute('aria-expanded', 'false');
    if (focusTrigger) this.triggerTarget.focus();
  }

  back() {
    this.showStep(this.step === 'day' ? 'month' : 'year');
  }

  pickYear(event) {
    this.tempYear = Number(event.currentTarget.dataset.year);
    this.showStep('month');
  }

  pickMonth(event) {
    const month = Number(event.currentTarget.dataset.month);
    if (this.modeValue === 'date') {
      this.tempMonth = month;
      this.showStep('day');
      return;
    }
    this.commit({ year: this.tempYear, month });
  }

  pickDay(event) {
    this.commit({
      year: this.tempYear,
      month: this.tempMonth,
      day: Number(event.currentTarget.dataset.day),
    });
  }

  keydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
      return;
    }

    const deltas = { ArrowRight: 1, ArrowLeft: -1 };
    if (!(event.key in deltas) && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const grid = event.target.closest('[data-grid]');
    if (!grid) return;

    const buttons = Array.from(grid.querySelectorAll('button:not(:disabled)'));
    const currentIndex = buttons.indexOf(document.activeElement);
    if (currentIndex < 0) return;

    const cols = Number(grid.dataset.cols) || 1;
    const delta = deltas[event.key] || (event.key === 'ArrowDown' ? cols : -cols);
    const next = buttons[currentIndex + delta];
    if (!next) return;

    event.preventDefault();
    next.focus();
  }


  onDocumentMousedown(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  showStep(step) {
    this.step = step;
    this.yearGridTarget.hidden = step !== 'year';
    this.monthGridTarget.hidden = step !== 'month';
    if (this.hasDayPanelTarget) this.dayPanelTarget.hidden = step !== 'day';
    this.backTarget.hidden = step === 'year';
    this.titleTarget.textContent = this.stepTitle(step);

    if (step === 'year') this.refreshYearGrid();
    if (step === 'month') this.refreshMonthGrid();
    if (step === 'day') this.buildDayGrid();

    // Focus the selected (or first enabled) cell so keyboard users can drive
    // the picker with arrows/Enter right away.
    requestAnimationFrame(() => {
      const grids = { year: this.yearGridTarget, month: this.monthGridTarget };
      if (this.hasDayGridTarget) grids.day = this.dayGridTarget;
      const grid = grids[step];
      if (!grid) return;
      const cell = grid.querySelector('[data-selected="true"]')
        || grid.querySelector('button:not(:disabled)');
      if (!cell) return;
      if (step === 'year') cell.scrollIntoView({ block: 'center' });
      cell.focus({ preventScroll: step === 'year' });
    });
  }

  stepTitle(step) {
    if (step === 'year') return 'Year';
    if (step === 'month') return 'Month';
    return `${this.monthsValue[this.tempMonth - 1]} ${this.tempYear}`;
  }

  refreshYearGrid() {
    this.yearGridTarget.querySelectorAll('button').forEach((button) => {
      this.markSelected(button, Number(button.dataset.year) === this.tempYear);
    });
  }

  refreshMonthGrid() {
    const min = this.parseIso(this.minValue);
    const max = this.parseIso(this.maxValue);

    this.monthGridTarget.querySelectorAll('button').forEach((button) => {
      const month = Number(button.dataset.month);
      button.disabled = (this.tempYear === min.year && month < min.month)
        || (this.tempYear === max.year && month > max.month);
      this.markSelected(button, month === this.tempMonth && this.tempYear !== null);
    });
  }

  buildDayGrid() {
    const selected = this.selectedParts();
    const min = this.parseIso(this.minValue);
    const max = this.parseIso(this.maxValue);
    const totalDays = new Date(this.tempYear, this.tempMonth, 0).getDate();
    const firstWeekday = new Date(this.tempYear, this.tempMonth - 1, 1).getDay();

    this.dayGridTarget.replaceChildren();

    for (let i = 0; i < firstWeekday; i += 1) {
      this.dayGridTarget.appendChild(document.createElement('span'));
    }

    for (let day = 1; day <= totalDays; day += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = day;
      button.className = 'ui-date-picker__cell ui-date-picker__cell--day';
      button.dataset.day = day;
      button.dataset.action = 'date-picker-component#pickDay';
      button.disabled = this.compare({ year: this.tempYear, month: this.tempMonth, day }, min) < 0
        || this.compare({ year: this.tempYear, month: this.tempMonth, day }, max) > 0;
      this.markSelected(button, !!selected && selected.day === day
        && selected.month === this.tempMonth && selected.year === this.tempYear);
      this.dayGridTarget.appendChild(button);
    }
  }

  markSelected(button, isSelected) {
    button.classList.toggle('ui-date-picker__cell--selected', isSelected);
    if (isSelected) button.dataset.selected = 'true';
    else delete button.dataset.selected;
  }

  commit({ year, month, day }) {
    const monthName = this.monthsValue[month - 1];
    const iso = day
      ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      : `${year}-${String(month).padStart(2, '0')}`;

    this.inputTarget.value = iso;
    this.triggerTextTarget.textContent = day ? `${day} ${monthName} ${year}` : `${monthName} ${year}`;
    this.triggerTextTarget.classList.remove('ui-date-picker__trigger-text--placeholder');
    this.dispatch('change', { detail: { value: iso, year, month, day } });
    this.close(true);
  }

  alignPopover() {
    const rect = this.element.getBoundingClientRect();
    const overflows = rect.left + POPOVER_WIDTH > window.innerWidth - POPOVER_MARGIN;
    this.popoverTarget.classList.toggle('ui-date-picker__popover--align-right', overflows);
  }

  selectedParts() {
    return this.parseIso(this.inputTarget.value);
  }

  parseIso(value) {
    const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(value || '');
    if (!match) return null;
    return { year: Number(match[1]), month: Number(match[2]), day: match[3] ? Number(match[3]) : null };
  }

  compare(a, b) {
    if (a.year !== b.year) return a.year - b.year;
    if (a.month !== b.month) return a.month - b.month;
    return (a.day || 0) - (b.day || 0);
  }
}
